import { computed, makeObservable, observable } from "mobx";
import { ChatMessageGroup } from "./ChatMessageGroup";
import { ChatMessage } from "./ChatMessage";
import { MessageAuthor } from "./MessageAuthor";
import {
    AcxAgentService,
    type AgentChatType,
    ChatSession,
} from "services/AcxAgentService";
import type {
    AnalysisMetadata,
    KboFlowFinding,
    KboFlowResearchPreview,
} from "services/AcxAgentService";
import type { AgentArtifact } from "./AgentArtifact";
import type { ChatActionsMap } from "./ChatAction";

type PersistedMessageAdditionalInformation = {
    DisplayMessage?: string;
    kind?: string;
    contentType?: string;
    Artifacts?: AgentArtifact[];
    /** Saved clip and montage players, keyed by action type */
    Actions?: Partial<ChatActionsMap>;
};

function parseMessageAdditionalInformation(
    additionalInformation?: string,
): PersistedMessageAdditionalInformation {
    try {
        const parsed = JSON.parse(additionalInformation ?? "{}");
        return parsed !== null && typeof parsed === "object" ? parsed : {};
    } catch {
        return {};
    }
}

function getRenderMode(
    messageType: string,
    additionalInformation: PersistedMessageAdditionalInformation,
) {
    return messageType === "Assistant" &&
        additionalInformation.kind === "kboFlowResearchReport" &&
        additionalInformation.contentType === "text/html"
        ? "htmlDocument" as const
        : undefined;
}

export type ChatNoMessages = Omit<ChatSession, "messages">;

export class Chat {
    /**
     * The persisted chat ID. New chats do not receive one until their first
     * displayable message or streamed request is persisted.
     */
    id: string;

    @observable createdOn: string;
    @observable firstName: string;
    @observable lastName: string;
    @observable username: string;
    @observable name: string | undefined;
    @observable type: AgentChatType | undefined;
    @observable kboFlowPreview?: KboFlowResearchPreview;
    @observable.ref kboFlowFindings?: KboFlowFinding[];

    @observable messageGroups: ChatMessageGroup[];

    /**
     * Present if this chat was generated as part of a kbo analysis during
     * subscribe to search.
     */
    @observable analysisMetadata?: AnalysisMetadata;
    /**
     * Whether this chat was generated as part of a kbo analysis during subsribe to search.
     */
    @computed get wasKboAnalysis() {
        return this.analysisMetadata !== undefined;
    }

    @computed get isKboFlowResearch() {
        return this.type === "KboFlowResearch";
    }

    readonly service: AcxAgentService;

    constructor() {
        makeObservable(this);

        this.service = new AcxAgentService();
        this.messageGroups = [];
    }

    private addMessageGroup(author: MessageAuthor) {
        const messageGroup = new ChatMessageGroup(this, author);
        this.messageGroups.push(messageGroup);
        return messageGroup;
    }

    private getRecentMessageGroup(author: MessageAuthor) {
        for (let i = this.messageGroups.length - 1; i >= 0; i--)
            if (this.messageGroups[i].author === author)
                return this.messageGroups[i];
    }

    @computed get hasUserMessaged() {
        return !!this.id && this.messageGroups.length > 1;
    }

    @computed get recentMessageGroup() {
        return this.messageGroups.at(-1);
    }

    @computed get recentAgentMessageGroup() {
        return this.getRecentMessageGroup(MessageAuthor.Agent);
    }

    @computed get recentUserMessageGroup() {
        return this.getRecentMessageGroup(MessageAuthor.User);
    }

    public addAgentMessageGroup() {
        return this.addMessageGroup(MessageAuthor.Agent);
    }

    public addUserMessageGroup() {
        return this.addMessageGroup(MessageAuthor.User);
    }

    public static fromChatSession(chatSession: ChatSession) {
        const chat = new Chat();
        chat.id = chatSession.id;
        let currentMessageGroup: ChatMessageGroup | null = null;
        chatSession.messages.forEach((msg, idx) => {
            if (msg.messageType !== "User" && msg.messageType !== "Assistant") {
                console.warn("Skipping system message");
                return;
            }
            const messageAuthor =
                msg.messageType === "Assistant"
                    ? MessageAuthor.Agent
                    : MessageAuthor.User;
            if (
                !currentMessageGroup ||
                currentMessageGroup.author !== messageAuthor
            ) {
                currentMessageGroup = chat.addMessageGroup(messageAuthor);
            }
            const parsedInfo = parseMessageAdditionalInformation(
                msg.additionalInformation,
            );
            const content = parsedInfo.DisplayMessage ?? msg.message;
            currentMessageGroup.addMessage(
                new ChatMessage(
                    currentMessageGroup,
                    content,
                    getRenderMode(msg.messageType, parsedInfo),
                ),
            );
            if (parsedInfo.Artifacts?.length) {
                currentMessageGroup.addArtifacts(parsedInfo.Artifacts);
            }
            // Only KBo's own messages carry players; a user message's info comes from the client
            const savedActions =
                messageAuthor === MessageAuthor.Agent
                    ? parsedInfo.Actions
                    : undefined;
            for (const [type, args] of Object.entries(savedActions ?? {})) {
                currentMessageGroup.actions.set(
                    type as keyof ChatActionsMap,
                    args,
                );
            }
            currentMessageGroup.setFeedback(
                msg.userFeedbackType,
                msg.userFeedback,
            );

            if (msg.suggestions && msg.suggestions.length > 0) {
                currentMessageGroup.addServerSuggestions(msg.suggestions);
            }
        });
        chat.createdOn = chatSession.createdOn;
        chat.firstName = chatSession.firstName;
        chat.lastName = chatSession.lastName;
        chat.username = chatSession.username;
        chat.name = chatSession.name;
        chat.type = chatSession.type;
        chat.kboFlowPreview = chatSession.kboFlowPreview;
        chat.kboFlowFindings = chatSession.kboFlowFindings;
        chat.analysisMetadata = chatSession.analysisMetadata;
        return chat;
    }

    public static fromChatNoMessage(chat: ChatNoMessages) {
        const newChat = new Chat();
        newChat.id = chat.id;
        newChat.createdOn = chat.createdOn;
        newChat.firstName = chat.firstName;
        newChat.lastName = chat.lastName;
        newChat.username = chat.username;
        newChat.name = chat.name;
        newChat.type = chat.type;
        newChat.kboFlowPreview = chat.kboFlowPreview;
        return newChat;
    }
}
